import { PageContext } from 'context/PageContext';
import { useContext, useMemo } from 'react';
import { useAppSelector } from 'store/hooks';
import { apiInstance, getAuthenticatedQuoteEndpoint, getBaseProxyUrl } from '../services/Api';

export interface SearchParams {
	params: [
		{
			param: string;
			search_config_lov: Lov[];
		},
	];
}

interface SpdSearchResponse {
	data?: {
		response?: Lov[] | Obj;
	};
}

export interface Template {
	templates: TemplateElement[];
}

export interface TemplateElement {
	fieldID: string;
	mandatory: boolean;
	label: string;
	lov: Lov[];
	value: string;
}

type SearchType = {
	name: string;
	params: SearchParam[];
};

type SearchParam = {
	param_name: string;
	param_value: string;
};

export interface Lov {
	label: string;
	value: string;
	default?: boolean;
}

export interface Obj {
	[key: string]: string | number | boolean | object;
}

type search_config_lov = Lov;

export interface SpdSearchInputField {
	param: string;
	value: string | number | boolean | object;
}

interface UseSpdSearchProps {
	spdSearchType: SpdSearchType;
}

export const useSpdSearch = ({ spdSearchType }: UseSpdSearchProps) => {
	const pageContext = useContext(PageContext);
	const isLoggedIn = useAppSelector((state) => state.customer.isLoggedIn);

	// Prefer the configured SPD URL; otherwise use the CMS endpoint with the proxy base URL.
	const url = useMemo(() => {
		const metaAreaContent = pageContext?.metaArea?.items?.[0];

		const configuredUrl = spdSearchType.searchConfiguration.url;
		if (configuredUrl) {
			return getAuthenticatedQuoteEndpoint(configuredUrl, isLoggedIn);
		}

		const endpoint = getAuthenticatedQuoteEndpoint(
			metaAreaContent?.content?.quoteSearchConfigurationApiEndpoint?.url || '',
			isLoggedIn
		);
		return getBaseProxyUrl(pageContext.apiEndpoints, endpoint) || '';
	}, [spdSearchType, pageContext, isLoggedIn]);

	const doSearch = async (
		searchType?: SearchType,
		configOverride?: SpdSearchType['searchConfiguration']
	): Promise<Lov[] | Obj> => {
		if (!url) {
			console.error('No URL defined for SPD search.');
			return [{ label: '', value: '' }];
		}
		// Override lets the same field's `secondarySearchConfiguration` decode responses correctly
		// (e.g. an object) even though the hook's primary config expects a LOV.
		const config = configOverride ?? spdSearchType.searchConfiguration;
		const method = config.httpMethod?.toLowerCase?.() || 'get';

		const responseType = config.responseType;

		try {
			const res: SpdSearchResponse = await apiInstance()[method](url, searchType);

			if (responseType === 'obj') {
				return res.data?.response;
			}

			return Array.isArray(res.data?.response)
				? res.data.response.map(({ label, value }) => ({ label, value }))
				: [];
		} catch (error) {
			console.error('SPD search failed:', error);
			throw error;
		}
	};

	return { doSearch, url };
};
